import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";

import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import { makeStyles } from "tss-react/mui";

import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import { showToast } from "../../../../../../store/actions";
import TableWithStickHeader from "../../../../../Common/Table/TableWithStickHeader";
import { setPrevState } from "../../../../../../util/common";
import { ButtonUI, KMKDialog } from "../../../../../Common";

const columns = [
  { id: "id", label: "#", width: "70px", align: "left" },
  { id: "attribute", label: "Attribute", width: "150px", align: "left" },
  { id: "message", label: "Message", width: "420px", align: "left" },
];

const useStyles = makeStyles()((theme) => ({
  dialogPaper: {
    "& .MuiPaper-root.MuiDialog-paper": {
      maxWidth: "960px",
      width: "960px",
    },
  },
  formContainer: {
    display: "grid",
    width: "100%",
    textAlign: "left",
    minHeight: "100px",
  },
  container: {
    padding: "24px 24px 24px 24px",
    backgroundColor: "#FFFFFF",
    minHeight: "100px",
  },
  Toolbar: {
    width: "100%",
    fontSize: "1.1rem",
    backgroundColor: "white",
    color: "black",
  },
}));

export default function ErrorLog({ configData }) {
  const [errorState, setErrorState] = useState({
    errorList: [],
  });
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const handleCancel = () => {
    configData.setOpenErrorLogPopup(false);
  };

  // Function to call on Proceed button click to load data by checking conflicted fields
  const handleProceedClick = () => {
    configData.dataTableRef.current.resetData();
    configData.setFields([]);
    const errorFields = [...configData.errorList];
    const selectedTemplate = configData.templateDataRef.current.find(
      (item) => item.name.toLowerCase() === configData.templateName.toLowerCase()
    );

    if (Object.keys(selectedTemplate).length > 0) {
      if (selectedTemplate?.fields?.length) {
        // Remove mappings and mdm for not matched fields
        selectedTemplate.fields.forEach((field) => {
          let eFieldIndex = errorFields.findIndex(
            ({ field: eField }) => eField.toLowerCase() === field.name.toLowerCase()
          );
          while (eFieldIndex !== -1) {
            field.mappings = field.mappings.filter(
              ({ dataSource }) => errorFields[eFieldIndex].dataSource.toLowerCase() !== dataSource.toLowerCase()
            );

            if (field.mappings.length === 0) {
              field.mdm = {};
            }

            if (field.mappings.length >= 2 && field.mdm && Object.keys(field.mdm).length > 0) {
              field.mdm.priority = field.mdm.priority.filter(
                (dataSource) => errorFields[eFieldIndex].dataSource.toLowerCase() !== dataSource.toLowerCase()
              );
            }

            errorFields.splice(eFieldIndex, 1);
            eFieldIndex = errorFields.findIndex(
              ({ field: eField }) => eField.toLowerCase() === field.name.toLowerCase()
            );
          }
        });

        selectedTemplate.fields.forEach((item) => {
          item.mappings = item.mappings.map((mapping) => {
            configData.dataSources.forEach((ds) => {
              if (Object.keys(mapping).length > 0 && ds.name.toLowerCase() === mapping.dataSource.toLowerCase()) {
                ds.fields.forEach((dsField) => {
                  if (Object.keys(mapping).length > 0 && dsField.name.toLowerCase() === mapping?.field?.toLowerCase()) {
                    let dsMapping = dsField.entity_mapping;
                    if (Object.keys(dsMapping).length !== 0) {
                      mapping = {};
                    }
                  }
                });
              }
            });
            return mapping;
          });

          item.mappings = item.mappings.filter((mapping) => Object.keys(mapping).length > 0);

          if (item.mappings.length <= 1) {
            item.mdm = {};
          }

          if (Object.keys(item.mdm).length > 0) {
            item.mdm.priority = item.mdm.priority.filter((ds) =>
              item.mappings.some(({ dataSource }) => ds.toLowerCase() === dataSource.toLowerCase())
            );
            if (item.mdm.priority.length === 1) {
              item.mdm = {};
            }
          }

          //Add Row to load data in attribute table
          configData.dataTableRef.current.addRow(item);
          configData.clearMatchingRules();
        });
      }

      configData.tagsRef.current.resetData();
      if (selectedTemplate?.tags?.length)
        configData.tagsRef.current.setTagsFromParent(JSON.parse(JSON.stringify(selectedTemplate.tags)));

      dispatch(
        showToast({
          message: "Template loaded successfully.",
          type: "success",
        })
      );
    }
    configData.setOpenErrorLogPopup(false);
  };

  useEffect(() => {
    let errorDataArr = [];

    configData.errorList.length &&
      configData.errorList.forEach((row, key) => {
        errorDataArr.push([
          {
            value: key + 1,
            align: "left",
          },
          {
            value: row.field,
            align: "left",
          },
          {
            value: row.message,
            align: "left",
          },
        ]);
      });

    setPrevState(setErrorState, { errorList: errorDataArr });
    return () => {
      setPrevState(setErrorState, { errorList: [] });
    };
  }, [configData.errorList]);

  return (
    <KMKDialog className={classes.dialogPaper} open={configData.open} onClose={handleCancel}>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar className={classes.Toolbar}>{configData.popupTitle} </Toolbar>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "black",
          }}
        >
          <CloseIcon />
        </IconButton>
      </AppBar>
      <Box sx={{ width: "100%" }} className={classes.container}>
        <Box sx={{ width: "100%" }} component="form" className={classes.formContainer}>
          <Grid container spacing={2}>
            <Grid sx={{ color: (theme) => theme.palette.error.main }} size={9}>
              There are some mismatch in mapping(s) or relationship(s). Are you sure you want to proceed?
            </Grid>
            <Grid size={3}>
              <DialogActions sx={{ padding: 0 }}>
                <ButtonUI variant="contained" color="secondary" onClick={handleCancel}>
                  Cancel
                </ButtonUI>
                <ButtonUI
                  onClick={() => {
                    handleProceedClick();
                  }}
                  variant="contained"
                  color="success"
                  style={{ marginLeft: 15 }}
                >
                  Proceed
                </ButtonUI>
              </DialogActions>
            </Grid>
            <Grid size={12}>
              <TableWithStickHeader
                {...{
                  columns: columns,
                  tableData: errorState.errorList,
                  tableMaxWidth: "912px",
                  tableMaxHeight: "350px",
                  noDataColspan: 2,
                  noDataMessage: "No error found",
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </KMKDialog>
  );
}
